/**
 * ActiveLiveSiteEventsCard.tsx
 * ----------------
 * Card displaying a table containing details about currently active live site events.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';

import { LiveSiteEventItem } from '../../models/ui/liveSiteEvent';
import { StatusIndicator } from '../shared/StatusIndicator';
import { Table } from './Table';
import { getTitleForGeography, isAllGeographiesCollection } from '../../utilities/geographies';
import { getDisplayNameForService } from '../../utilities/services';
import { formatUtcAsLocalInEnglishLocale } from '../../utilities/time';

/**
 * Component interfaces
 */

interface ActiveLiveSiteEventsCardProps {
    liveSiteEvents: LiveSiteEventItem[];
    liveSiteEventUrl: (id: number) => string;
}

/**
 * Card displaying a table containing details about currently active live site events.
 * @param props Component properties
 * @returns Rendered content
 */
export function ActiveLiveSiteEventsCard (props: ActiveLiveSiteEventsCardProps) {
    const { liveSiteEvents, liveSiteEventUrl } = props;

    return (
        <div className="active-live-site-events-card ms-depth-8">
            <Table
                columns={[
                    {
                        cellClassName: 'active-live-site-events-card__severity active-live-site-events-card__cell',
                        headerClassName: 'active-live-site-events-card__severity active-live-site-events-card__severity--header',
                        hideOnReflow: true,
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => <StatusIndicator
                            severity={liveSiteEvent.severity}
                            state="Active"
                            svgStyles={{ height: 20, width: 20 }} />,
                        /* eslint-enable react/display-name */
                        title: 'Sev',
                    },
                    {
                        cellClassName: 'active-live-site-events-card__title active-live-site-events-card__cell',
                        headerClassName: 'active-live-site-events-card__title active-live-site-events-card__title--header',
                        hideOnReflow: true,
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent =>
                            <Link
                                className="active-live-site-events-card__link ms-Link"
                                to={liveSiteEventUrl(liveSiteEvent.id)}>
                                {liveSiteEvent.title}
                            </Link>,
                        /* eslint-enable react/display-name */
                        title: 'Title',
                    },
                    {
                        cellClassName: 'active-live-site-events-card__service active-live-site-events-card__cell',
                        headerClassName: 'active-live-site-events-card__service active-live-site-events-card__service--header',
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => <span>{getDisplayNameForService(liveSiteEvent.service)}</span>,
                        /* eslint-enable react/display-name */
                        title: 'Service',
                    },
                    {
                        cellClassName: 'active-live-site-events-card__start-time active-live-site-events-card__cell',
                        headerClassName: 'active-live-site-events-card__start-time active-live-site-events-card__start-time--header',
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => <span>{formatUtcAsLocalInEnglishLocale(liveSiteEvent.impactStartTimestamp)}</span>,
                        /* eslint-enable react/display-name */
                        title: 'Start Time',
                    },
                    {
                        cellClassName: 'active-live-site-events-card__last-update active-live-site-events-card__cell',
                        headerClassName: 'active-live-site-events-card__last-update active-live-site-events-card__last-update--header',
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => <span>{formatUtcAsLocalInEnglishLocale(liveSiteEvent.lastUpdateTimestamp)}</span>,
                        /* eslint-enable react/display-name */
                        title: 'Last Update',
                    },
                    {
                        cellClassName: 'active-live-site-events-card__geographies active-live-site-events-card__cell',
                        headerClassName: 'active-live-site-events-card__geographies active-live-site-events-card__geographies--header',
                        /* eslint-disable react/display-name */
                        // Justification: this isn't really a component.
                        render: liveSiteEvent => {
                            const geographies = liveSiteEvent.impacts.map(impact => impact.geography);

                            if (isAllGeographiesCollection(geographies)) {
                                return <span>All geographies</span>;
                            }

                            return <span>{geographies.map(g => getTitleForGeography(g)).sort().join(', ')}</span>;
                        },
                        /* eslint-enable react/display-name */
                        title: 'Geographies',
                    }
                ]}
                data={liveSiteEvents}
                name="Active live site events"
                reflowOptions={{
                    /* eslint-disable react/display-name */
                    // Justification: this isn't really a component.
                    renderSideSection: liveSiteEvent => <StatusIndicator
                        severity={liveSiteEvent.severity}
                        state="Active"
                        svgStyles={{ height: 20, width: 20 }} />,
                    renderTitle: liveSiteEvent =>
                        <Link
                            className="active-live-site-events-card__link ms-Link"
                            to={liveSiteEventUrl(liveSiteEvent.id)}>
                            {liveSiteEvent.title}
                        </Link>,
                    /* eslint-enable react/display-name */
                }}
                sortable={false} />
        </div>
    );
}
